import {
  BUILT_INDEX_STEP_LABELS,
  type BuiltIndexStep,
} from "@/lib/built-index-analytics";

/** Milestones tracked per cohort, in funnel order. */
export const COHORT_MILESTONES: BuiltIndexStep[] = [
  "lead_gate_view",
  "lead_gate_submit",
  "assessment_built",
  "results_view",
  "checkout_start",
  "unlock",
];

export const cohortMilestoneLabel = (step: BuiltIndexStep) =>
  BUILT_INDEX_STEP_LABELS[step] ?? step;

export type CohortEventInput = {
  id: string;
  occurred_at: string;
  step: string;
  session_id?: string | null;
  email?: string | null;
};

export type CohortRow = {
  /** ISO week start (Monday) in YYYY-MM-DD form — the cohort key. */
  weekStart: string;
  label: string;
  size: number;
  /** Unique members of the cohort that reached each milestone. */
  reached: Record<string, number>;
  /** Percent of the cohort that reached each milestone (0-100, one decimal). */
  rates: Record<string, number>;
};

/** Stable identity for a funnel participant. */
const identify = (e: CohortEventInput) => e.session_id || e.email || e.id;

/** Monday 00:00 UTC of the week containing `date`, as YYYY-MM-DD. */
export function weekStartOf(date: Date | string): string {
  const d = new Date(date);
  const utc = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const day = new Date(utc).getUTCDay(); // 0 = Sunday
  const shift = (day + 6) % 7; // days since Monday
  return new Date(utc - shift * 86_400_000).toISOString().slice(0, 10);
}

const pct = (n: number, d: number) => (d === 0 ? 0 : Math.round((n / d) * 1000) / 10);

const formatWeekLabel = (weekStart: string) => {
  const start = new Date(`${weekStart}T00:00:00Z`);
  const end = new Date(start.getTime() + 6 * 86_400_000);
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
  return `${fmt(start)} – ${fmt(end)}`;
};

/**
 * Group funnel events into signup-week cohorts.
 *
 * A participant's cohort is the week of their FIRST recorded event, so later
 * conversions always count toward the week they entered the funnel. This is
 * what makes conversion rates comparable over time instead of being smeared
 * across whatever week the purchase happened to land in.
 */
export function buildCohorts(
  events: CohortEventInput[],
  milestones: BuiltIndexStep[] = COHORT_MILESTONES,
): CohortRow[] {
  const firstSeen = new Map<string, number>();
  const stepsById = new Map<string, Set<string>>();

  for (const e of events) {
    const key = identify(e);
    const ts = new Date(e.occurred_at).getTime();
    if (Number.isNaN(ts)) continue;
    const prev = firstSeen.get(key);
    if (prev === undefined || ts < prev) firstSeen.set(key, ts);
    const set = stepsById.get(key) ?? new Set<string>();
    set.add(e.step);
    stepsById.set(key, set);
  }

  const cohorts = new Map<string, string[]>();
  for (const [key, ts] of firstSeen) {
    const week = weekStartOf(new Date(ts));
    cohorts.set(week, [...(cohorts.get(week) ?? []), key]);
  }

  return [...cohorts.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([weekStart, members]) => {
      const reached: Record<string, number> = {};
      const rates: Record<string, number> = {};
      for (const step of milestones) {
        const n = members.filter((m) => stepsById.get(m)?.has(step)).length;
        reached[step] = n;
        rates[step] = pct(n, members.length);
      }
      return {
        weekStart,
        label: formatWeekLabel(weekStart),
        size: members.length,
        reached,
        rates,
      };
    });
}

/** Flat rows for CSV export. */
export function cohortCsvRows(cohorts: CohortRow[], milestones: BuiltIndexStep[] = COHORT_MILESTONES) {
  return cohorts.map((c) => {
    const row: Record<string, unknown> = {
      cohort_week_start: c.weekStart,
      cohort_week: c.label,
      cohort_size: c.size,
    };
    for (const step of milestones) {
      row[`${step}_count`] = c.reached[step] ?? 0;
      row[`${step}_pct`] = c.rates[step] ?? 0;
    }
    return row;
  });
}
